/**
 * ReviewActionModal - Shared modal for approve/reject actions in admin tables
 *
 * SESSION_352: Admin Review Queue Table Implementation
 *
 * Used by:
 * - AliasReviewTable
 * - AddressCorrectionTable
 * - BusinessClaimTable
 *
 * Provides consistent approve/reject UI across all admin review tables.
 */
import React, { useState, useCallback } from 'react';
import {
  Modal,
  Stack,
  Text,
  Textarea,
  Group,
  Button,
  Box
} from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';

/** Minimum characters for rejection reason */
const MIN_REJECTION_REASON_LENGTH = 10;

/**
 * Props for ReviewActionModal
 */
export interface ReviewActionModalProps {
  /** Modal open state */
  opened: boolean;
  /** Close handler */
  onClose: () => void;
  /** Action type: approve or reject */
  actionType: 'approve' | 'reject';
  /** Item display name for confirmation text */
  itemName: string;
  /** Submit handler for approve (adminNotes is optional) */
  onApprove?: (adminNotes?: string) => Promise<void>;
  /** Submit handler for reject (reason required, adminNotes optional) */
  onReject?: (rejectionReason: string, adminNotes?: string) => Promise<void>;
  /** Whether the form is submitting */
  isSubmitting?: boolean;
  /** Whether to show admin notes field for approve action */
  showApproveNotes?: boolean;
  /** Whether to show admin notes field for reject action */
  showRejectNotes?: boolean;
  /** Custom approval confirmation text */
  approveConfirmText?: string;
  /** Custom rejection confirmation text */
  rejectConfirmText?: string;
}

/**
 * ReviewActionModal Component
 *
 * Reusable modal for approve/reject actions with consistent UI.
 */
export const ReviewActionModal: React.FC<ReviewActionModalProps> = ({
  opened,
  onClose,
  actionType,
  itemName,
  onApprove,
  onReject,
  isSubmitting = false,
  showApproveNotes = true,
  showRejectNotes = true,
  approveConfirmText,
  rejectConfirmText
}): React.ReactElement => {
  const [adminNotes, setAdminNotes] = useState<string>('');
  const [rejectionReason, setRejectionReason] = useState<string>('');

  const isApprove = actionType === 'approve';

  // Reset form when modal opens
  const handleClose = useCallback((): void => {
    setAdminNotes('');
    setRejectionReason('');
    onClose();
  }, [onClose]);

  // Handle approve submit
  const handleApproveSubmit = useCallback(async (): Promise<void> => {
    if (!onApprove) return;
    await onApprove(adminNotes || undefined);
    handleClose();
  }, [onApprove, adminNotes, handleClose]);

  // Handle reject submit
  const handleRejectSubmit = useCallback(async (): Promise<void> => {
    if (!onReject || rejectionReason.trim().length < MIN_REJECTION_REASON_LENGTH) return;
    await onReject(rejectionReason, adminNotes || undefined);
    handleClose();
  }, [onReject, rejectionReason, adminNotes, handleClose]);

  const isRejectValid = rejectionReason.trim().length >= MIN_REJECTION_REASON_LENGTH;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={isApprove ? 'Approve Item' : 'Reject Item'}
      centered
      size="md"
      styles={{
        title: {
          fontWeight: 600,
          color: isApprove
            ? 'var(--mantine-color-green-7)'
            : 'var(--mantine-color-red-7)'
        }
      }}
    >
      <Box
        p="md"
        style={{
          backgroundColor: isApprove
            ? 'var(--mantine-color-green-0)'
            : 'var(--mantine-color-red-0)',
          borderRadius: '8px',
          border: isApprove
            ? '1px solid var(--mantine-color-green-3)'
            : '1px solid var(--mantine-color-red-3)'
        }}
      >
        <Stack gap="md">
          {/* Confirmation text */}
          <Text size="sm" fw={500} c={isApprove ? 'green.8' : 'red.8'}>
            {isApprove
              ? approveConfirmText || `Approve "${itemName}"?`
              : rejectConfirmText || `Reject "${itemName}"?`
            }
          </Text>

          {/* Reject: Reason field (required) */}
          {!isApprove && (
            <>
              <Textarea
                label="Rejection Reason"
                placeholder="Explain why this item is being rejected..."
                value={rejectionReason}
                onChange={(e): void => setRejectionReason(e.target.value)}
                required
                rows={3}
                error={
                  rejectionReason.length > 0 && !isRejectValid
                    ? `Rejection reason must be at least ${MIN_REJECTION_REASON_LENGTH} characters`
                    : undefined
                }
              />
              <Text size="xs" c="dimmed">
                {rejectionReason.trim().length}/{MIN_REJECTION_REASON_LENGTH} minimum characters
              </Text>
            </>
          )}

          {/* Admin notes (optional) */}
          {((isApprove && showApproveNotes) || (!isApprove && showRejectNotes)) && (
            <Textarea
              label="Admin Notes (optional)"
              placeholder="Add any internal notes..."
              value={adminNotes}
              onChange={(e): void => setAdminNotes(e.target.value)}
              rows={2}
            />
          )}

          {/* Action buttons */}
          <Group gap="sm" justify="flex-end">
            <Button
              variant="subtle"
              color="gray"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            {isApprove ? (
              <Button
                color="green"
                leftSection={<IconCheck size={16} />}
                onClick={handleApproveSubmit}
                loading={isSubmitting}
              >
                Confirm Approve
              </Button>
            ) : (
              <Button
                color="red"
                leftSection={<IconX size={16} />}
                onClick={handleRejectSubmit}
                loading={isSubmitting}
                disabled={!isRejectValid}
              >
                Confirm Reject
              </Button>
            )}
          </Group>
        </Stack>
      </Box>
    </Modal>
  );
};
